import { deleteDB, openDB, type DBSchema, type IDBPDatabase } from 'idb';
import { createDefaultSettings, defaultCompany, type AppSettings, type Invoice, type Payment, type Quote } from '../types';
import { formatDocumentNumber, parseDocumentSequence, type DocumentNumberKind } from './documentFlow';

interface QuoteFlowDB extends DBSchema {
  quotes: {
    key: string;
    value: Quote;
    indexes: { 'by-updated': string; 'by-sync-state': string };
  };
  settings: {
    key: string;
    value: AppSettings;
  };
  invoices: {
    key: string;
    value: Invoice;
    indexes: { 'by-updated': string; 'by-quote': string };
  };
  payments: {
    key: string;
    value: Payment;
    indexes: { 'by-invoice': string; 'by-created': string };
  };
}

const openQuoteFlowDB = (name: string) => openDB<QuoteFlowDB>(name, 2, {
  upgrade(db, oldVersion) {
    if (oldVersion < 1) {
      const quotes = db.createObjectStore('quotes', { keyPath: 'id' });
      quotes.createIndex('by-updated', 'updatedAt');
      quotes.createIndex('by-sync-state', 'syncState');
      db.createObjectStore('settings', { keyPath: 'key' });
    }
    if (oldVersion < 2) {
      const invoices = db.createObjectStore('invoices', { keyPath: 'id' });
      invoices.createIndex('by-updated', 'updatedAt');
      invoices.createIndex('by-quote', 'quoteId');
      const payments = db.createObjectStore('payments', { keyPath: 'id' });
      payments.createIndex('by-invoice', 'invoiceId');
      payments.createIndex('by-created', 'createdAt');
    }
  },
});

const legacyDatabase = openQuoteFlowDB('quoteflow');
let activeDatabase: Promise<IDBPDatabase<QuoteFlowDB>> | null = null;
let activeOwner: string | null = null;
let ownerEpoch=0;
export const assertLocalDataOwner = (userId: string) => { if (activeOwner !== userId) throw new Error('Session changed'); };
export const captureLocalDataScope = () => {
  const owner=activeOwner,epoch=ownerEpoch;
  return () => { if(!owner||owner!==activeOwner||epoch!==ownerEpoch)throw new Error('Session changed'); };
};

const database = () => {
  if (!activeDatabase) throw new Error('กรุณาเข้าสู่ระบบก่อนใช้งานข้อมูลในเครื่อง');
  return activeDatabase;
};

export function setLocalDataOwner(userId: string | null) {
  if(activeOwner!==userId)ownerEpoch++;
  activeOwner = userId;
  activeDatabase = userId ? openQuoteFlowDB(`quoteflow-user-${userId}`) : null;
}

export async function deleteLocalDataForOwner(userId: string) {
  ownerEpoch++;
  const current = activeDatabase;
  activeDatabase = null;
  activeOwner = null;
  if (current) (await current).close();
  await deleteDB(`quoteflow-user-${userId}`);
  await deleteDB(`bilaflow-r2-catalog-${userId}`);
}

export async function getLegacyDataSummary() {
  const [quotes, invoices, payments] = await Promise.all([
    legacyDatabase.then((db) => db.count('quotes')),
    legacyDatabase.then((db) => db.count('invoices')),
    legacyDatabase.then((db) => db.count('payments')),
  ]);
  return { quotes, invoices, payments, hasData: quotes + invoices + payments > 0 };
}

export async function migrateLegacyDataToActive() {
  const target = await database();
  const [targetQuotes, targetInvoices, targetPayments] = await Promise.all([
    target.count('quotes'), target.count('invoices'), target.count('payments'),
  ]);
  if (targetQuotes + targetInvoices + targetPayments > 0) return { migrated: false, reason: 'target_not_empty' as const };

  const source = await legacyDatabase;
  const [quotes, invoices, payments, settings] = await Promise.all([
    source.getAll('quotes'), source.getAll('invoices'), source.getAll('payments'), source.get('settings', 'app'),
  ]);
  if (quotes.length + invoices.length + payments.length === 0 && !settings) return { migrated: false, reason: 'legacy_empty' as const };

  const tx = target.transaction(['quotes', 'settings', 'invoices', 'payments'], 'readwrite');
  for (const quote of quotes) await tx.objectStore('quotes').put(quote);
  for (const invoice of invoices) await tx.objectStore('invoices').put(invoice);
  for (const payment of payments) await tx.objectStore('payments').put(payment);
  if (settings) await tx.objectStore('settings').put(settings);
  await tx.done;
  return { migrated: true, quotes: quotes.length, invoices: invoices.length, payments: payments.length };
}

export async function listQuotes(includeDeleted = false) {
  const rows = await (await database()).getAll('quotes');
  return rows
    .filter((quote) => includeDeleted || !quote.deletedAt)
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export async function saveQuote(quote: Quote) {
  const tx = (await database()).transaction('quotes', 'readwrite');
  const rows = await tx.store.getAll();
  if (rows.some((row) => row.id !== quote.id && row.number.trim().toLocaleLowerCase() === quote.number.trim().toLocaleLowerCase())) {
    throw new Error('เลขที่เอกสารนี้ถูกใช้งานแล้ว');
  }
  await tx.store.put(quote);
  await tx.done;
  return quote;
}

export async function saveQuotes(quotes: Quote[]) {
  const tx = (await database()).transaction('quotes', 'readwrite');
  await Promise.all([...quotes.map((quote) => tx.store.put(quote)), tx.done]);
}

export async function listInvoices(includeDeleted = false) {
  const rows = await (await database()).getAll('invoices');
  return rows.filter((invoice) => includeDeleted || !invoice.deletedAt)
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export async function saveInvoice(invoice: Invoice) {
  const tx = (await database()).transaction('invoices', 'readwrite');
  const rows = await tx.store.getAll();
  if (rows.some((row) => row.id !== invoice.id && row.number.trim().toLocaleLowerCase() === invoice.number.trim().toLocaleLowerCase())) {
    throw new Error('เลขที่ใบแจ้งหนี้นี้ถูกใช้งานแล้ว');
  }
  await tx.store.put(invoice);
  await tx.done;
  return invoice;
}

export async function markInvoiceDeleted(id: string,localOnly=false) {
  const db = await database();
  const invoice = await db.get('invoices', id);
  if (!invoice) return;
  const now = new Date().toISOString();
  await db.put('invoices', { ...invoice, deletedAt: now, trashExpiresAt:new Date(Date.now()+86_400_000).toISOString(),purgedAt:null,localTrashOnly:localOnly&&invoice.cloudRevision===undefined, updatedAt: now, version: invoice.version + 1, syncState: 'pending' });
}

export async function saveInvoices(invoices: Invoice[]) {
  const tx = (await database()).transaction('invoices', 'readwrite');
  await Promise.all([...invoices.map((invoice) => tx.store.put(invoice)), tx.done]);
}

export async function listPayments() {
  const rows = await (await database()).getAll('payments');
  return rows.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

export async function savePayment(payment: Payment) {
  const tx = (await database()).transaction('payments', 'readwrite');
  const rows = await tx.store.getAll();
  if (rows.some((row) => row.id !== payment.id && row.receiptNumber.trim().toLocaleLowerCase() === payment.receiptNumber.trim().toLocaleLowerCase())) {
    throw new Error('เลขที่ใบเสร็จนี้ถูกใช้งานแล้ว');
  }
  await tx.store.put(payment);
  await tx.done;
  return payment;
}

export async function reserveDocumentNumber(kind: DocumentNumberKind, date = new Date()) {
  const db = await database();
  const tx = db.transaction(['settings', 'quotes', 'invoices', 'payments'], 'readwrite');
  const settingsStore = tx.objectStore('settings');
  const defaults = createDefaultSettings();
  const stored = await settingsStore.get('app');
  const settings: AppSettings = stored ? { ...defaults, ...stored, company: { ...defaultCompany, ...stored.company } } : defaults;
  const year = date.getFullYear();
  let configuredSequence = 0;
  let usedNumbers: string[] = [];

  if (kind === 'quote') {
    configuredSequence = settings.quoteSequenceYear === year ? settings.lastQuoteSequence : 0;
    usedNumbers = (await tx.objectStore('quotes').getAll()).map((row) => row.number);
  } else if (kind === 'invoice') {
    configuredSequence = settings.invoiceSequenceYear === year ? settings.lastInvoiceSequence : 0;
    usedNumbers = (await tx.objectStore('invoices').getAll()).map((row) => row.number);
  } else {
    configuredSequence = settings.receiptSequenceYear === year ? settings.lastReceiptSequence : 0;
    usedNumbers = (await tx.objectStore('payments').getAll()).map((row) => row.receiptNumber);
  }

  const existingMaximum = usedNumbers.reduce((maximum, number) => Math.max(maximum, parseDocumentSequence(kind, year, number)), 0);
  const sequence = Math.max(configuredSequence, existingMaximum) + 1;
  if (kind === 'quote') { settings.quoteSequenceYear = year; settings.lastQuoteSequence = sequence; }
  else if (kind === 'invoice') { settings.invoiceSequenceYear = year; settings.lastInvoiceSequence = sequence; }
  else { settings.receiptSequenceYear = year; settings.lastReceiptSequence = sequence; }
  settings.cloudSettingsUpdatedAt = new Date().toISOString();
  await settingsStore.put(settings);
  await tx.done;
  return { number: formatDocumentNumber(kind, year, sequence), sequence, settings };
}

export async function savePayments(payments: Payment[]) {
  const tx = (await database()).transaction('payments', 'readwrite');
  await Promise.all([...payments.map((payment) => tx.store.put(payment)), tx.done]);
}

export async function markQuoteDeleted(id: string,localOnly=false) {
  const db = await database();
  const quote = await db.get('quotes', id);
  if (!quote) return;
  const now = new Date().toISOString();
  await db.put('quotes', {
    ...quote,
    deletedAt: now,
    trashExpiresAt:new Date(Date.now()+86_400_000).toISOString(),purgedAt:null,
    localTrashOnly:localOnly&&quote.cloudRevision===undefined,
    updatedAt: now,
    version: quote.version + 1,
    syncState: 'pending',
  });
}

export async function getSettings(): Promise<AppSettings> {
  const db = await database();
  const settings = await db.get('settings', 'app');
  const defaults = createDefaultSettings();
  if (settings) return { ...defaults, ...settings, company: { ...defaultCompany, ...settings.company } };
  const initial = defaults;
  try { await db.put('settings', initial); }
  catch (error) { if (!(error instanceof DOMException) || error.name !== 'QuotaExceededError') throw error; }
  return initial;
}

export async function saveSettings(settings: AppSettings) {
  await (await database()).put('settings', settings);
}

export async function restoreLocalDocument(kind:'quote'|'invoice',id:string){
  const db=await database();const store=kind==='quote'?'quotes':'invoices';
  const tx=db.transaction(store,'readwrite');const record=await tx.store.get(id);
  if(!record?.deletedAt||record.purgedAt||!record.trashExpiresAt||Date.now()>=Date.parse(record.trashExpiresAt))throw new Error('cloud_trash_expired');
  if(record.cloudRevision!==undefined&&record.syncState==='synced')throw new Error('cloud_restore_requires_online');
  const sameNumber=(await tx.store.getAll()).some(value=>value.id!==id&&value.number.trim().toLowerCase()===record.number.trim().toLowerCase());
  if(sameNumber)throw new Error('document_number_conflict');
  const restored={...record,deletedAt:null,trashExpiresAt:null,updatedAt:new Date().toISOString(),version:record.version+1,syncState:'pending' as const};
  await tx.store.put(restored);await tx.done;
}

// Only documents explicitly deleted in Local-only mode are removed locally.
// A pending deletion of a previously cloud-backed record must remain available
// to send to the server; removing that command could resurrect the cloud copy.
export async function purgeExpiredLocalOnlyTrash(){
  const owner=activeOwner;const db=await database();
  if(owner!==activeOwner)throw new Error('Session changed');
  const tx=db.transaction(['quotes','invoices','payments'],'readwrite');
  const removedInvoices=new Set<string>();
  for(const kind of ['quotes','invoices'] as const)for(const record of await tx.objectStore(kind).getAll()){
    if(record.localTrashOnly&&record.cloudRevision===undefined&&record.deletedAt&&record.trashExpiresAt&&Date.parse(record.trashExpiresAt)<=Date.now()){
      await tx.objectStore(kind).delete(record.id);if(kind==='invoices')removedInvoices.add(record.id);
    }
  }
  if(removedInvoices.size)for(const payment of await tx.objectStore('payments').getAll())if(removedInvoices.has(payment.invoiceId))await tx.objectStore('payments').delete(payment.id);
  await tx.done;
}

// All-or-nothing: quota exhaustion must not leave half the restored invoices,
// payments or settings in Local. Existing unsynced originals are not cleared.
export async function saveSyncSnapshot(quotes: Quote[], invoices: Invoice[], payments: Payment[], settings: AppSettings, userId: string) {
  assertLocalDataOwner(userId);
  const db=await database();
  assertLocalDataOwner(userId);
  const tx = db.transaction(['quotes','invoices','payments','settings'], 'readwrite');
  const purgedInvoices=new Set(invoices.filter(value=>value.purgedAt).map(value=>value.id));
  if(purgedInvoices.size)for(const payment of await tx.objectStore('payments').getAll()){
    if(purgedInvoices.has(payment.invoiceId))await tx.objectStore('payments').delete(payment.id);
  }
  await Promise.all([
    ...quotes.map((value) => tx.objectStore('quotes').put(value)),
    ...invoices.map((value) => tx.objectStore('invoices').put(value)),
    ...payments.map((value) => tx.objectStore('payments').put(value)),
    tx.objectStore('settings').put(settings),
    tx.done,
  ]);
}

export async function replaceLocalData(quotes: Quote[], settings?: AppSettings, invoices: Invoice[] = [], payments: Payment[] = []) {
  const db = await database();
  const tx = db.transaction(['quotes', 'settings', 'invoices', 'payments'], 'readwrite');
  await tx.objectStore('quotes').clear();
  await tx.objectStore('invoices').clear();
  await tx.objectStore('payments').clear();
  for (const quote of quotes) await tx.objectStore('quotes').put(quote);
  for (const invoice of invoices) await tx.objectStore('invoices').put(invoice);
  for (const payment of payments) await tx.objectStore('payments').put(payment);
  if (settings) await tx.objectStore('settings').put({ ...createDefaultSettings(), ...settings, company: { ...defaultCompany, ...settings.company } });
  await tx.done;
}
